import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ListFilter } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Combobox,
  Pagination,
  SearchInput,
  Segmented,
  type SegmentedOption,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import { cn } from "@rumbledirect/ui-v2/cn";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { useGetAdminOrders } from "@/lib/api/generated/order/order";
import type { OrderRow } from "@/orders/order";
import { type OrderColumn, getOrderColumns } from "@/orders/orderColumns";
import {
  ORDER_VIEWS,
  ORDER_VIEW_LABELS,
  type OrderView,
  isOrderView,
  toOrderViewQuery,
} from "@/orders/orderViews";
import { useOrderStatusLookup } from "@/orders/useOrderStatusLookup";

/** Server page size. Kept within the endpoint maximum of 30. */
const ITEMS_PER_PAGE = 20;

const VIEW_OPTIONS: SegmentedOption<OrderView>[] = ORDER_VIEWS.map((value) => ({
  value,
  label: ORDER_VIEW_LABELS[value],
}));

/** One skeleton per row of a full page, so the table keeps its height when the rows arrive. */
const SKELETON_ROW_KEYS = Array.from(
  { length: ITEMS_PER_PAGE },
  (_, index) => `order-skeleton-${index}`,
);

interface OrdersSearch {
  readonly page: number;
  readonly view: OrderView;
  /** A workflow phase, by its technical name; undefined means every phase. */
  readonly status: string | undefined;
  /** Free-text search term; undefined means unfiltered. */
  readonly query: string | undefined;
}

function toOptionalString(value: unknown): string | undefined {
  // TanStack Router's default search codec parses a numeric-looking URL value
  // (e.g. a freshly-loaded `?query=123456`) into a number, and a `?x=true`
  // into a boolean, while values written through `navigate()` stay strings.
  // Both forms are accepted; arrays and objects are not a search term.
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return typeof value === "string" && value !== "" ? value : undefined;
}

export const Route = createFileRoute("/_authenticated/orders")({
  // The whole filter state lives in the URL so a view can be shared or
  // bookmarked. The list opens on what is still on its way rather than on
  // everything: it is a work list, and the question it answers first is which
  // cars are still to come.
  validateSearch: (search: Record<string, unknown>): OrdersSearch => {
    const page = Number(search.page);

    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      view: isOrderView(search.view) ? search.view : "open",
      status: toOptionalString(search.status),
      query: toOptionalString(search.query),
    };
  },
  component: OrdersPage,
});

function OrdersPage(): ReactNode {
  const { page, view, status, query } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    lookup,
    isPending: statusesPending,
    isError: statusesErrored,
    refetch: refetchStatuses,
  } = useOrderStatusLookup();

  // Local search box synced to the URL with a small debounce so each keystroke
  // does not push a history entry or refetch.
  const [searchInput, setSearchInput] = useState(query ?? "");
  useEffect(() => {
    setSearchInput(query ?? "");
  }, [query]);
  useEffect(() => {
    if (searchInput === (query ?? "")) return;
    const timer = window.setTimeout(() => {
      void navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          query: searchInput === "" ? undefined : searchInput,
        }),
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, query, navigate]);

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminOrders(
      {
        page,
        itemsPerPage: ITEMS_PER_PAGE,
        ...toOrderViewQuery(view),
        ...(status ? { "workflowStatus[]": [status] } : {}),
        ...(query ? { search: query } : {}),
      },
      { query: { placeholderData: keepPreviousData } },
    );

  const mayOpen = hasPermission(useCurrentUser(), "admin_order_read_details");
  const columns = getOrderColumns(lookup, statusesPending, mayOpen);
  // A phase from a bookmarked URL that the brief does not know (retired, or the
  // brief failed) is still sent to the API, so it must stay visible and
  // clearable in the filter under its raw name.
  const statusOptions = useMemo(() => {
    const options = [...lookup].map(([value, meta]) => ({
      value,
      label: meta.label,
    }));
    if (status && !lookup.has(status)) {
      options.push({ value: status, label: status });
    }
    return options;
  }, [lookup, status]);
  const isBusy = isPending || isPlaceholderData;

  // A bookmarked page beyond the last one (the result set shrank) would show an
  // empty table without any pagination controls, so settle on the last page.
  const totalItems = data?.totalItems;
  useEffect(() => {
    if (isPlaceholderData || totalItems === undefined) return;
    const lastPage = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    if (page > lastPage) {
      void navigate({
        search: (prev) => ({ ...prev, page: lastPage }),
        replace: true,
      });
    }
  }, [isPlaceholderData, totalItems, page, navigate]);

  /** Any filter change resets to page 1; page 7 of the old result set is meaningless. */
  const applyFilters = (patch: Partial<OrdersSearch>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  };

  return (
    <>
      {/* Visually hidden; the AppShell topbar already shows the title visually. */}
      <h1 className="sr-only">Bestellingen</h1>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={view}
          onValueChange={(next) => applyFilters({ view: next })}
          options={VIEW_OPTIONS}
          size="sm"
          aria-label="Weergave van de bestellingen"
        />

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <SearchInput
            className="w-full sm:w-80"
            value={searchInput}
            placeholder="Zoek op kenteken, voertuig of dealer"
            onChange={(event) => setSearchInput(event.target.value)}
            onClear={() => setSearchInput("")}
          />
          <Combobox
            aria-label="Status"
            value={status}
            onValueChange={(next) =>
              applyFilters({ status: toOptionalString(next) })
            }
            options={statusOptions}
            loading={statusesPending}
            placeholder="Alle statussen"
            searchable={false}
            icon={<ListFilter className="size-4" />}
            triggerClassName="w-full sm:w-56"
          />
        </div>
      </div>

      {statusesErrored && (
        <div
          role="alert"
          className="mb-4 flex items-center gap-2 text-sm text-muted-foreground"
        >
          <span>Statussen konden niet worden geladen.</span>
          <button
            type="button"
            onClick={refetchStatuses}
            className="font-medium text-primary transition-colors hover:text-primary/80"
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {/* Outside the busy table body, so the announcement itself is not suppressed. */}
      <p role="status" aria-live="polite" className="sr-only">
        {isBusy ? "Bestellingen worden geladen" : ""}
      </p>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              {columns.map((column) => (
                <Th key={column.id} className={column.visibilityClassName}>
                  {column.header}
                </Th>
              ))}
            </Tr>
          </THead>
          <TBody
            aria-busy={isBusy}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <OrderRows
              columns={columns}
              isPending={isPending}
              isError={isError}
              members={data?.member}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      <Pagination
        className="mt-4"
        page={page}
        pageSize={ITEMS_PER_PAGE}
        totalItems={data?.totalItems ?? 0}
        status={isPending ? "pending" : isError ? "error" : "ready"}
        onPageChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, page: next }) })
        }
      />
    </>
  );
}

function OrderRows({
  columns,
  isPending,
  isError,
  members,
  onRetry,
}: {
  readonly columns: readonly OrderColumn[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly OrderRow[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        {columns.map((column) => (
          <Td key={column.id} className={column.visibilityClassName}>
            <Skeleton className="h-4 w-24" />
          </Td>
        ))}
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={columns.length} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de bestellingen niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (!members || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={columns.length} className="py-10 text-center">
          <p className="text-muted-foreground">
            Geen bestellingen met deze filters.
          </p>
        </Td>
      </Tr>
    );
  }

  return members.map((row) => (
    <Tr key={row.id}>
      {columns.map((column) => (
        <Td
          key={column.id}
          className={cn(column.visibilityClassName, column.cellClassName)}
        >
          {column.cell(row)}
        </Td>
      ))}
    </Tr>
  ));
}
